import type { LucideIcon } from 'lucide-react'
import {
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  Compass,
  FileSearch,
  Globe,
  Home,
  LifeBuoy,
  Megaphone,
  Plane,
  Sparkles,
  Star,
  Tag,
  Users,
  Wrench,
} from 'lucide-react'

import type { SidebarNavKey } from './portal-navigation'

export type NavChild = { label: string; href: string }

export type NavItem = {
  key: SidebarNavKey | 'partner-profile'
  label: string
  href: string
  icon: LucideIcon
  /** Optional expandable sub-nav (collapsible group in the sidebar). */
  children?: NavChild[]
}

/** Primary portal navigation (matches the Member Portal design). */
export const mainNav: NavItem[] = [
  { key: 'home', label: 'Home', href: '/home', icon: Home },
  { key: 'partners', label: 'Partners', href: '/partners', icon: FileSearch },
  { key: 'learning', label: 'Learning', href: '/learning', icon: BookOpen },
  { key: 'adx', label: 'ADX Booking Platform', href: '/adx', icon: Plane },
  { key: 'marketing', label: 'Marketing', href: '/marketing', icon: Megaphone },
  { key: 'events', label: 'Events', href: '/events', icon: Star },
  { key: 'calendar', label: 'Calendar', href: '/calendar', icon: Calendar },
  { key: 'promotions', label: 'Promotions', href: '/promotions', icon: Tag },
  {
    key: 'business-services',
    label: 'Business Services',
    href: '/business-services',
    icon: Briefcase,
  },
  {
    key: 'partner-programs',
    label: 'Partner Programs',
    href: '/partner-programs',
    icon: Users,
    children: [
      { label: 'Air', href: '/partner-programs/air' },
      { label: 'Cruise', href: '/partner-programs/cruise' },
      { label: 'Hotel', href: '/partner-programs/hotel' },
      { label: 'DMC', href: '/partner-programs/dmc' },
      { label: 'FAMs', href: '/partner-programs/fams' },
      { label: 'Insurance', href: '/partner-programs/insurance' },
    ],
  },
  { key: 'analytics', label: 'Analytics', href: '/analytics', icon: BarChart3 },
  { key: 'tools', label: 'Tools & Resources', href: '/tools', icon: Wrench },
  { key: 'ensemble', label: 'Ensemble', href: '/ensemble', icon: Globe },
]

/** Secondary nav pinned to the bottom of the sidebar. */
export const footerNav: NavItem[] = [
  { key: 'whats-new', label: "What's New", href: '/whats-new', icon: Sparkles },
  { key: 'help', label: 'Help', href: '/help', icon: LifeBuoy },
  {
    key: 'getting-started',
    label: 'Getting Started',
    href: '/getting-started',
    icon: Compass,
  },
]

/** Account-context routes shown outside the primary sidebar. */
export const accountNav: NavItem[] = [
  { key: 'partner-profile', label: 'Partner Profile', href: '/partner-profile', icon: Building2 },
]

/** Flattened (incl. sub-nav) — used for pathname → title lookups. */
export const allNav: NavItem[] = [
  ...mainNav.flatMap((item) => [
    item,
    ...(item.children?.map((c) => ({ ...c, key: item.key, icon: item.icon })) ?? []),
  ]),
  ...footerNav,
  ...accountNav,
]

/** Applies CMS order while preserving every code-defined link exactly once. */
export function orderNavItems(items: readonly NavItem[], order: readonly string[] | undefined) {
  const byKey = new Map(items.map((item) => [item.key, item]))
  const seen = new Set<string>()
  const ordered: NavItem[] = []

  for (const key of order ?? []) {
    const item = byKey.get(key as NavItem['key'])
    if (!item || seen.has(key)) continue

    seen.add(key)
    ordered.push(item)
  }

  for (const item of items) {
    if (!seen.has(item.key)) ordered.push(item)
  }

  return ordered
}
